'use client'

import { Section } from '../layout/Section'
import { Container } from '../layout/Container'
import { Card } from '../common/Card'
import { FadeIn } from '../motion/FadeIn'
import { StaggerChildren } from '../motion/StaggerChildren'

interface Testimonial {
  quote: string
  author: {
    name: string
    title: string
    company?: string
    avatar?: string
  }
}

interface TestimonialsProps {
  sectionNumber?: string
  heading: string
  subheading?: string
  testimonials: Testimonial[]
}

export function Testimonials({
  sectionNumber,
  heading,
  subheading,
  testimonials
}: TestimonialsProps) {
  return (
    <Section number={sectionNumber}>
      <Container>
        {/* Section Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <FadeIn>
            <h2 className="headline-lg mb-4">{heading}</h2>
          </FadeIn>
          {subheading && (
            <FadeIn delay={0.1}>
              <p className="body-lg">{subheading}</p>
            </FadeIn>
          )}
        </div>

        {/* Testimonials Grid */}
        <StaggerChildren
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          staggerDelay={0.08}
        >
          {testimonials.map((testimonial, index) => (
            <Card key={index} className="flex flex-col">
              {/* Quote */}
              <blockquote className="flex-1 text-[var(--landing-text-secondary)] leading-relaxed mb-6">
                "{testimonial.quote}"
              </blockquote>

              {/* Author */}
              <div className="flex items-center gap-3">
                {testimonial.author.avatar ? (
                  <img
                    src={testimonial.author.avatar}
                    alt={testimonial.author.name}
                    className="w-10 h-10 rounded-full"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[var(--accent)]/10 flex items-center justify-center">
                    <span className="text-sm font-medium text-[var(--accent)]">
                      {testimonial.author.name.charAt(0)}
                    </span>
                  </div>
                )}

                <div>
                  <p className="text-sm font-medium text-[var(--landing-text-primary)]">
                    {testimonial.author.name}
                  </p>
                  <p className="text-xs text-[var(--landing-text-muted)]">
                    {testimonial.author.title}
                    {testimonial.author.company && ` at ${testimonial.author.company}`}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </StaggerChildren>
      </Container>
    </Section>
  )
}
